import React, { useEffect, useState, useMemo } from "react";
import "bootstrap-daterangepicker/daterangepicker.css";
import Chart from "react-apexcharts";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import PredefinedDateRanges from "../../core/common/range-picker/datePicker";
import store from "../../store/store";
import useStore from "../../store/store";
import ReportServices from "../../services/ReportServices";

// Register the necessary components for Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);
const NewDashboard = () => {
  const { user } = store((state) => state);
  const selectedStore = useStore((state) => state.selectedStore);
  const storeId = selectedStore?._id;

  // Chart configuration for customer overview
  const customerChart = {
    chart: {
      type: "radialBar",
      height: 130,
      sparkline: {
        enabled: true,
      },
    },
    plotOptions: {
      radialBar: {
        hollow: {
          margin: 0,
          size: "60%",
        },
        track: {
          background: "#e7e7e7",
          strokeWidth: "67%",
          margin: 0,
        },
        dataLabels: {
          show: false,
        },
      },
    },
    colors: ["#ff6b35", "#28a745"],
    labels: ["First Time", "Return"],
    legend: {
      show: false,
    },
  };

  // Add loading state
  const [isLoading, setIsLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [topEmployees, setTopEmployees] = useState([]);
  const [topServices, setTopServices] = useState([]);
  const [paymentMethodData, setPaymentMethodData] = useState(null);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 30)),
    endDate: new Date(),
  });

  // Calculate series data for customer chart
  const series = useMemo(() => {
    if (!dashboardData) return [0, 0];

    const firstTime = dashboardData.firstTimeCustomers || 0;
    const returnCustomers = dashboardData.returnCustomers || 0;
    const total = firstTime + returnCustomers;

    if (total === 0) return [0, 0];

    // Calculate percentages for the radial chart
    const firstTimePercentage = Math.round((firstTime / total) * 100);
    const returnPercentage = Math.round((returnCustomers / total) * 100);

    return [firstTimePercentage, returnPercentage];
  }, [dashboardData]);

  // Improved fetchDashboardData with proper error handling
  const fetchDashboardData = async (startDate, endDate) => {
    try {
      setIsLoading(true);
      if (!storeId) return null;

      const response = await ReportServices.getDashboardTotals(
        storeId,
        startDate,
        endDate
      );
      setDashboardData(response);

      // Fetch top employees dynamically
      const topEmpRes = await ReportServices.getTopEmployees(
        storeId,
        startDate,
        endDate
      );
      setTopEmployees(topEmpRes);

      // Fetch top services dynamically
      const topServRes = await ReportServices.getTopServices(
        storeId,
        startDate,
        endDate
      );
      setTopServices(topServRes);

      // Fetch payment method totals
      const paymentRes = await ReportServices.getPaymentMethodTotals(
        storeId,
        startDate,
        endDate
      );
      setPaymentMethodData(paymentRes);

      return response;
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  // Enhanced date change handler
  const handleDateChange = async (newStartDate, newEndDate) => {
    const validStartDate = new Date(newStartDate);
    const validEndDate = new Date(newEndDate);

    if (isNaN(validStartDate.getTime()) || isNaN(validEndDate.getTime())) {
      console.error("Invalid date format");
      return;
    }

    setDateRange({
      startDate: validStartDate,
      endDate: validEndDate,
    });

    await fetchDashboardData(validStartDate, validEndDate);
  };

  const getDates = async (dates) => {
    await handleDateChange(dates.startDate, dates.endDate);
  };

  // Initial data fetch
  useEffect(() => {
    if (storeId && dateRange.startDate && dateRange.endDate) {
      fetchDashboardData(dateRange.startDate, dateRange.endDate);
    }
    // eslint-disable-next-line
  }, [storeId]);

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-2">
          <div className="mb-3">
            <h1 className="mb-1">Welcome, {user?.name}</h1>
          </div>
          <div className="input-icon-start position-relative mb-3">
            <span className="input-icon-addon fs-16 text-gray-9">
              <i className="ti ti-calendar" />
            </span>
            <PredefinedDateRanges onDateChange={getDates} />
          </div>
        </div>
        <div className="row">
          <div className="col-xl-3 col-sm-6 col-12 d-flex">
            <div className="card bg-primary sale-widget flex-fill">
              <div className="card-body d-flex align-items-center">
                <span className="sale-icon bg-white text-primary">
                  <i className="ti ti-file-text fs-24" />
                </span>{" "}
                <div className="ms-2">
                  <p className="text-white mb-1">Total Sales</p>
                  <div className="d-inline-flex align-items-center flex-wrap gap-2">
                    <h4 className="text-white">
                      {isLoading ? (
                        <span className="loading-text">Loading...</span>
                      ) : dashboardData?.totalSales ? (
                        `₹${dashboardData.totalSales.toLocaleString()}`
                      ) : (
                        "₹0"
                      )}
                    </h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-xl-3 col-sm-6 col-12 d-flex">
            <div className="card bg-secondary sale-widget flex-fill">
              <div className="card-body d-flex align-items-center">
                <span className="sale-icon bg-white text-secondary">
                  <i className="ti ti-repeat fs-24" />
                </span>{" "}
                <div className="ms-2">
                  <p className="text-white mb-1">Total Customers</p>
                  <div className="d-inline-flex align-items-center flex-wrap gap-2">
                    <h4 className="text-white">
                      {isLoading ? (
                        <span className="loading-text">Loading...</span>
                      ) : dashboardData?.totalCustomers !== undefined &&
                        dashboardData?.totalCustomers !== null ? (
                        dashboardData.totalCustomers.toLocaleString()
                      ) : (
                        "0"
                      )}
                    </h4>
                    <small className="text-white">
                      (
                      {isLoading ? (
                        <span className="loading-text">...</span>
                      ) : (
                        dashboardData?.totalInvoices || 0
                      )}{" "}
                      invoices)
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-xl-3 col-sm-6 col-12 d-flex">
            <div className="card bg-teal sale-widget flex-fill">
              <div className="card-body d-flex align-items-center">
                <span className="sale-icon bg-white text-teal">
                  <i className="ti ti-receipt fs-24" />
                </span>{" "}
                <div className="ms-2">
                  <p className="text-white mb-1">Total Expenses</p>
                  <div className="d-inline-flex align-items-center flex-wrap gap-2">
                    <h4 className="text-white">
                      {isLoading ? (
                        <span className="loading-text">Loading...</span>
                      ) : dashboardData?.totalExpenses ? (
                        `₹${dashboardData.totalExpenses.toLocaleString()}`
                      ) : (
                        "₹0"
                      )}
                    </h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-xl-3 col-sm-6 col-12 d-flex">
            <div className="card bg-info sale-widget flex-fill">
              <div className="card-body d-flex align-items-center">
                <span className="sale-icon bg-white text-info">
                  <i className="ti ti-calendar-check fs-24" />
                </span>{" "}
                <div className="ms-2">
                  <p className="text-white mb-1">Total Appointments</p>
                  <div className="d-inline-flex align-items-center flex-wrap gap-2">
                    <h4 className="text-white">
                      {isLoading ? (
                        <span className="loading-text">Loading...</span>
                      ) : dashboardData?.totalAppointments !== undefined ? (
                        dashboardData.totalAppointments.toLocaleString()
                      ) : (
                        "0"
                      )}
                    </h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-xl-12 col-sm-12 col-12 d-flex">
            <div className="card flex-fill">
              <div className="card-header">
                <div className="d-inline-flex align-items-center">
                  <span className="title-icon bg-soft-info fs-16 me-2">
                    <i className="ti ti-info-circle" />
                  </span>
                  <h5 className="card-title mb-0">Payment Modes</h5>
                </div>
              </div>
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-4">
                    <div className="info-item border bg-light p-3 text-center">
                      <div className="mb-3 text-info fs-24">
                        <i className="ti ti-credit-card" />
                      </div>
                      <p className="mb-1">Card</p>
                      <h5>
                        {isLoading ? (
                          <span className="loading-text">Loading...</span>
                        ) : paymentMethodData?.card ? (
                          `₹${paymentMethodData.card.totalAmount.toLocaleString()}`
                        ) : (
                          "₹0"
                        )}
                      </h5>
                      <small className="text-muted">
                        {paymentMethodData?.card?.totalInvoices || 0} invoices
                      </small>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="info-item border bg-light p-3 text-center">
                      <div className="mb-3 text-orange fs-24">
                        <i className="ti ti-cash" />
                      </div>
                      <p className="mb-1">Cash</p>
                      <h5>
                        {isLoading ? (
                          <span className="loading-text">Loading...</span>
                        ) : paymentMethodData?.cash ? (
                          `₹${paymentMethodData.cash.totalAmount.toLocaleString()}`
                        ) : (
                          "₹0"
                        )}
                      </h5>
                      <small className="text-muted">
                        {paymentMethodData?.cash?.totalInvoices || 0} invoices
                      </small>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="info-item border bg-light p-3 text-center">
                      <div className="mb-3 text-teal fs-24">
                        <i className="ti ti-device-mobile" />
                      </div>
                      <p className="mb-1">UPI</p>
                      <h5>
                        {isLoading ? (
                          <span className="loading-text">Loading...</span>
                        ) : paymentMethodData?.upi ? (
                          `₹${paymentMethodData.upi.totalAmount.toLocaleString()}`
                        ) : (
                          "₹0"
                        )}
                      </h5>
                      <small className="text-muted">
                        {paymentMethodData?.upi?.totalInvoices || 0} invoices
                      </small>
                    </div>
                  </div>
                </div>
                <div className="card-footer pb-sm-0">
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                    <h6>Customers Overview</h6>
                  </div>
                  <div className="row align-items-center">
                    <div className="col-sm-5">
                      <div id="customer-chart">
                        <Chart
                          options={customerChart}
                          series={series}
                          type="radialBar"
                          height={130}
                        />
                      </div>
                    </div>
                    <div className="col-sm-7">
                      <div className="row gx-0">
                        <div className="col-sm-6">
                          <div className="text-center border-end">
                            <h2 className="mb-1">
                              {isLoading ? (
                                <span className="loading-text">...</span>
                              ) : dashboardData?.firstTimeCustomers !==
                                  undefined &&
                                dashboardData?.firstTimeCustomers !== null ? (
                                dashboardData.firstTimeCustomers.toLocaleString()
                              ) : (
                                "0"
                              )}
                            </h2>
                            <p className="text-orange mb-2">First Time</p>
                            <span className="badge badge-success badge-xs d-inline-flex align-items-center">
                              <i className="ti ti-arrow-up-left me-1" />
                              {series[0]}%
                            </span>
                          </div>
                        </div>
                        <div className="col-sm-6">
                          <div className="text-center">
                            <h2 className="mb-1">
                              {isLoading ? (
                                <span className="loading-text">...</span>
                              ) : dashboardData?.returnCustomers !==
                                  undefined &&
                                dashboardData?.returnCustomers !== null ? (
                                dashboardData.returnCustomers.toLocaleString()
                              ) : (
                                "0"
                              )}
                            </h2>
                            <p className="text-teal mb-2">Return</p>
                            <span className="badge badge-success badge-xs d-inline-flex align-items-center">
                              <i className="ti ti-arrow-up-left me-1" />
                              {series[1]}%
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-xl-6 col-sm-12 col-12 d-flex">
            <div className="card flex-fill">
              <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-3">
                <div className="d-inline-flex align-items-center">
                  <span className="title-icon bg-soft-orange fs-16 me-2">
                    <i className="ti ti-flag" />
                  </span>
                  <h5 className="card-title mb-0">Recent Transactions</h5>
                </div>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-borderless custom-table">
                    <thead className="thead-light">
                      <tr>
                        <th
                          style={{ textAlign: "center", padding: "12px 8px" }}
                        >
                          #
                        </th>
                        <th
                          style={{ textAlign: "center", padding: "12px 8px" }}
                        >
                          Top Selling Service
                        </th>
                        <th
                          style={{ textAlign: "center", padding: "12px 8px" }}
                        >
                          Count
                        </th>
                        <th
                          style={{ textAlign: "center", padding: "12px 8px" }}
                        >
                          Total Amount
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {isLoading ? (
                        <tr>
                          <td
                            colSpan="4"
                            style={{ textAlign: "center", padding: "12px 8px" }}
                          >
                            Loading...
                          </td>
                        </tr>
                      ) : topServices && topServices.length > 0 ? (
                        topServices.map((service, idx) => (
                          <tr
                            key={service.serviceName + idx}
                            style={{ borderBottom: "1px solid #e9ecef" }}
                          >
                            <td
                              style={{
                                padding: "12px 8px",
                                textAlign: "center",
                              }}
                            >
                              {idx + 1}
                            </td>
                            <td
                              style={{
                                padding: "12px 8px",
                                textAlign: "center",
                              }}
                            >
                              {service.serviceName}
                            </td>
                            <td
                              style={{
                                padding: "12px 8px",
                                textAlign: "center",
                              }}
                            >
                              {service.count}
                            </td>
                            <td
                              className="fs-16 fw-bold text-gray-9"
                              style={{
                                padding: "12px 8px",
                                textAlign: "center",
                              }}
                            >
                              ₹{service.totalAmount?.toLocaleString()}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan="4"
                            style={{ textAlign: "center", padding: "12px 8px" }}
                          >
                            No data found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
          <div className="col-xl-6 col-sm-12 col-12 d-flex">
            <div className="card flex-fill">
              <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-3">
                <div className="d-inline-flex align-items-center">
                  <span className="title-icon bg-soft-orange fs-16 me-2">
                    <i className="ti ti-users" />
                  </span>
                  <h5 className="card-title mb-0">Top Employees</h5>
                </div>
              </div>
              <div className="card-body">
                {isLoading ? (
                  <div>Loading Top Employees...</div>
                ) : topEmployees && topEmployees.length > 0 ? (
                  topEmployees.map((emp, idx) => (
                    <div
                      key={emp.employeeName + idx}
                      className="d-flex align-items-center justify-content-between border-bottom mb-3 pb-3 flex-wrap gap-2"
                    >
                      <div className="d-flex align-items-center">
                        <div
                          className="avatar avatar-lg flex-shrink-0 bg-primary text-white d-flex align-items-center justify-content-center"
                          style={{ fontWeight: 600, fontSize: 20 }}
                        >
                          {emp.employeeName?.charAt(0) || "?"}
                        </div>
                        <div className="ms-2">
                          <h6 className="fs-14 fw-bold mb-1">
                            {emp.employeeName}
                          </h6>
                          <div className="d-flex align-items-center item-list">
                            <p className="d-inline-flex align-items-center">
                              <i className="ti ti-map-pin me-1" />
                              {emp.saloonName}
                            </p>
                            <p>{emp.totalCustomers} Customers</p>
                          </div>
                        </div>
                      </div>
                      <div className="text-end">
                        <h5>₹{emp.totalAmount?.toLocaleString()}</h5>
                      </div>
                    </div>
                  ))
                ) : (
                  <div>No employee data found.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="copyright-footer d-flex align-items-center justify-content-between border-top bg-white gap-3 flex-wrap">
        <p className="fs-13 text-gray-9 mb-0">
          2014-2025 © Style Buzz Pro. All Right Reserved
        </p>
        <p>Designed &amp; Developed By Style Buzz Pro </p>
      </div>
    </div>
  );
};

export default NewDashboard;
